/**
 * 文言の探索と差し込み (admin-english-toggle-20260924 / ADR-081)。
 *
 * 探索順は「選んだ言語 → 日本語 (正本) → キーそのもの」。英語の辞書は型で `ja` と同じ形を
 * 強制しているが、実行時に欠けていても (動的なキー・将来の辞書の取り違え) 画面を空にしない。
 */
import { namespaceDictionary } from './registry';
import {
  DEFAULT_LOCALE,
  type DictionaryTree,
  type HandleKey,
  type HandleParamsArgs,
  type Locale,
  type NamespaceHandle,
  type TranslationParams,
} from './types';

/** 言語ごとの辞書の組。テストでは欠けた辞書を渡して fallback を確かめる。 */
export type DictionarySet = Readonly<Record<Locale, DictionaryTree>>;

/** ドット区切りのキーで辞書の葉を引く。葉でない / 無いときは undefined。 */
export function lookupMessage(tree: DictionaryTree, key: string): string | undefined {
  let node: string | DictionaryTree | undefined = tree;
  for (const segment of key.split('.')) {
    if (node === undefined || typeof node === 'string') return undefined;
    if (!Object.prototype.hasOwnProperty.call(node, segment)) return undefined;
    node = node[segment];
  }
  return typeof node === 'string' ? node : undefined;
}

/**
 * 文言の `{name}` を差し込み値で置き換える。値が無い placeholder は残す
 * (欠けていることが画面で分かるようにし、空文字で黙って消さない)。
 */
export function interpolate(message: string, params?: TranslationParams): string {
  if (!params) return message;
  return message.replace(/\{(\w+)\}/g, (placeholder, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : placeholder,
  );
}

/**
 * 辞書の組を指定して文言を引く (純関数)。
 * 選んだ言語に無ければ日本語、日本語にも無ければキーそのものを返す。
 */
export function translateWith(
  dictionaries: DictionarySet,
  locale: Locale,
  key: string,
  params?: TranslationParams,
): string {
  const message =
    lookupMessage(dictionaries[locale], key) ??
    lookupMessage(dictionaries[DEFAULT_LOCALE], key) ??
    key;
  return interpolate(message, params);
}

/** 開発 / テストで未登録の名前空間を 1 回だけ知らせる (本番は黙ってキーを返す)。 */
const warnedNamespaces = new Set<string>();

function warnUnregistered(namespace: string): void {
  if (!import.meta.env?.DEV || warnedNamespaces.has(namespace)) return;
  warnedNamespaces.add(namespace);
  // 開発時だけの診断 (名前空間の handle を import し忘れた)
  console.warn(
    `i18n: 名前空間 "${namespace}" が登録されていません (i18n/namespaces/${namespace}.ts の handle を import してください)`,
  );
}

/**
 * アプリの辞書で文言を引く。キーは `名前空間.キー` (例: `shell.header.help`)。
 * 名前空間は registry から引く (`i18n/namespaces/*.ts` が import されたときに登録される)。
 * 未登録の名前空間はキーそのものを返す (画面を空にしない)。
 */
export function translate(locale: Locale, key: string, params?: TranslationParams): string {
  const dot = key.indexOf('.');
  const namespace = dot < 0 ? key : key.slice(0, dot);
  const rest = dot < 0 ? '' : key.slice(dot + 1);
  const ja = namespaceDictionary(namespace, DEFAULT_LOCALE);
  if (!ja) {
    warnUnregistered(namespace);
    return interpolate(key, params);
  }
  const selected = namespaceDictionary(namespace, locale) ?? ja;
  const message = lookupMessage(selected, rest) ?? lookupMessage(ja, rest) ?? key;
  return interpolate(message, params);
}

/** 名前空間を固定した翻訳関数の型。キーと差し込み値は handle の日本語辞書から型で検査される。 */
export type HandleTFunction<H extends NamespaceHandle> = <K extends HandleKey<H>>(
  key: K,
  ...params: HandleParamsArgs<H, K>
) => string;

/**
 * 名前空間を固定した翻訳関数を作る (hook が使えない場所 — 表示用の関数・定数の組み立て — 向け)。
 * 画面の中では `useT` を使う。
 */
export function createT<H extends NamespaceHandle>(locale: Locale, handle: H): HandleTFunction<H> {
  return (key, ...params) => translate(locale, `${handle.name}.${key}`, params[0]);
}
